import type { ISoundPlayer, SoundId } from './sound-player'
import { SOUND_CATALOG, type SoundCatalogEntry } from './sound-catalog'

export interface HtmlAudioOptions {
  catalog?: Record<SoundId, SoundCatalogEntry | { url: string }>
  /** True = drop new plays while one is in flight. Mirrors legacy `blocking`. */
  blocking?: boolean
  /** 0–100. Mirrors legacy `volume`. */
  volume?: number
}

export interface HtmlAudioSoundPlayer extends ISoundPlayer {
  setVolume(v: number): void
  getVolume(): number
  /** Hard mute (companion-active). Wins over instance and per-call volume. */
  setMuted(muted: boolean): void
  isPlaying(): boolean
  activeCount(): number
}

function clamp01(v: number): number {
  return Math.min(1, Math.max(0, v))
}

export function createHtmlAudioSoundPlayer(opts: HtmlAudioOptions = {}): HtmlAudioSoundPlayer {
  const catalog = opts.catalog ?? SOUND_CATALOG
  const active = new Map<number, HTMLAudioElement>()
  let nextKey = 1
  const blocking = opts.blocking ?? true
  let volume = clamp01((opts.volume ?? 60) / 100)
  let muted = false
  let unlocked = false

  function urlFor(id: SoundId): string | undefined {
    const entry = catalog[id]
    if (!entry) return undefined
    return 'url' in entry ? entry.url : undefined
  }

  return {
    async preload(ids) {
      for (const id of ids) {
        const url = urlFor(id)
        if (!url) continue
        const audio = new Audio(url)
        audio.preload = 'auto'
        audio.load()
      }
    },
    async play(id, playVolume) {
      if (muted) return
      const effective = playVolume !== undefined ? clamp01(playVolume / 100) : volume
      if (effective === 0) return
      const url = urlFor(id)
      if (!url) return
      if (blocking && active.size > 0) return

      const key = nextKey++
      let audio: HTMLAudioElement
      try {
        audio = new Audio(url)
        audio.volume = effective
      } catch {
        return
      }

      const cleanup = () => {
        active.delete(key)
        ;['canplaythrough', 'ended', 'error', 'abort'].forEach((ev) =>
          audio.removeEventListener(ev, cleanup as EventListener),
        )
      }
      audio.addEventListener('canplaythrough', () => {
        audio.play().catch(() => cleanup())
      })
      audio.addEventListener('ended', cleanup)
      audio.addEventListener('error', cleanup)
      audio.addEventListener('abort', cleanup)
      active.set(key, audio)
    },
    async unlock() {
      try {
        const probe = new Audio()
        probe.muted = true
        await probe.play()
        probe.pause()
        unlocked = true
      } catch {
        unlocked = false
      }
    },
    isUnlocked() {
      return unlocked
    },
    setVolume(v) {
      volume = clamp01(v / 100)
    },
    setMuted(m) {
      muted = m
    },
    getVolume() {
      return volume
    },
    isPlaying() {
      return active.size > 0
    },
    activeCount() {
      return active.size
    },
    dispose() {
      for (const audio of active.values()) {
        try {
          audio.pause()
        } catch {
          /* noop */
        }
      }
      active.clear()
    },
  }
}
